<template>
    <div id="appointments" class="appointments-grid" :class="{'md:mt-4': filteredAppointments.length === 0}">
        <appointments-nav
                v-if="filteredAppointments.length > 0"
                :start-date="startDate"
                :fixed="fixedNav"
                :left="fixedLeft"
                :right="fixedRight"
                @back="loadPrevAppointments"
                @forward="loadNextAppointments"
        />

        <div ref="days" class="appointment-days" v-if="filteredAppointments.length > 0">
            <div v-for="(day, index) in getDays()" :key="index" class="appointment-day">
                <div class="day-name">{{ day.format('ddd') }}</div>
                <div class="day-date">{{ day.format('Do MMM') }}</div>
            </div>
        </div>

        <div ref="fixedDays" class="appointment-days-fixed" v-if="filteredAppointments.length > 0">
            <div class="appointment-days-fixed-shadow"></div>

            <div v-for="(day, index) in getDays()" :key="index" class="appointment-day">
                <div class="day-name">{{ day.format('ddd') }}</div>
                <div class="day-date">{{ day.format('Do MMM') }}</div>
            </div>
        </div>

        <div v-if="filteredAppointments.length === 0" class="text-center p-8">
            <span class="block">Sorry, there are currently no appointments available.</span>
            <span class="block mt-4">Call 0330 678 0850 if you would like us to help you find a suitable appointment.</span>
        </div>

        <clinics v-else :days="getDays()" />

        <div class="appointments-loading" v-if="loadingAppointments">
            <div class="loader"></div>
        </div>
    </div>
</template>

<script>
    import { mapActions, mapState } from 'vuex';

    import DateTime from '../../lib/DateTime';

    import AppointmentsNav from './AppointmentsNav';
    import Clinics from './Clinics';

    export default {
        name: 'appointments-grid',

        components: {
            AppointmentsNav,
            Clinics,
        },

        data() {
            return {
                fixedNav: false,
                fixedLeft: 0,
                fixedRight: 0,
                scrollBarWidth: null,
            };
        },

        computed: {
            ...mapState('appointment', [
                'date',
                'place',
                'sort',
            ]),

            ...mapState('appointments', [
                'appointments',
                'loadingAppointments',
            ]),

            filteredAppointments() {
                return this.$store.getters['appointments/filteredAppointments'];
            },

            startDate() {
                return (this.date || window.moment()).startOf('day');
            },
        },

        mounted() {
            this.$gtm.trackView('Search Results Page', '/search');

            this.onWindowEvent();

            window.addEventListener('scroll', this.onWindowEvent);
            window.addEventListener('resize', this.onWindowEvent);
        },

        destroyed() {
            window.removeEventListener('scroll', this.onWindowEvent);
            window.removeEventListener('resize', this.onWindowEvent);
        },

        methods: {
            ...mapActions('appointment', ['setDate']),

            onWindowEvent() {
                if ( ! this.$refs.days) {
                    return;
                }

                if (this.scrollBarWidth === null) {
                    this.scrollBarWidth = this.getScrollbarWidth() || 0;
                }

                const bounds = this.$refs.days.getBoundingClientRect();

                const scrollTop = window.pageYOffset || document.body.scrollTop;
                const scrollLeft = window.pageXOffset || document.body.scrollLeft;

                const topOffset = bounds.top + scrollTop;
                const leftOffset = bounds.left + scrollLeft;
                const rightOffset = window.innerWidth - bounds.right - this.scrollBarWidth;

                if (scrollTop > topOffset) {
                    this.fixedNav = true;
                    this.fixedLeft = leftOffset;
                    this.fixedRight = rightOffset;

                    this.$refs.fixedDays.classList.add('visible');
                    this.$refs.fixedDays.style.left = `${leftOffset}px`;
                    this.$refs.fixedDays.style.right = `${rightOffset}px`;
                } else {
                    this.fixedNav = false;
                    this.fixedLeft = 0;
                    this.fixedRight = 0;

                    this.$refs.fixedDays.classList.remove('visible');
                }
            },

            getScrollbarWidth() {
                const outer = document.createElement('div');

                outer.style.visibility = 'hidden';
                outer.style.overflow = 'scroll';
                outer.style.msOverflowStyle = 'scrollbar';

                document.body.appendChild(outer);

                const inner = document.createElement('div');
                outer.appendChild(inner);

                const scrollbarWidth = (outer.offsetWidth - inner.offsetWidth);

                outer.parentNode.removeChild(outer);

                return scrollbarWidth;
            },

            getDays() {
                const start = this.startDate;

                // Skip Sundays.
                if (start.weekday() === 0) {
                    start.add(1, 'day');
                }

                const date = window.moment(start);

                let i = 1, days = [ this.startDate ];

                while (i < 5) {
                    date.add(1, 'day');

                    if (date.weekday() === 0) {
                        continue;
                    }

                    days.push(window.moment(date.unix() * 1000));

                    i++;
                }

                return days;
            },

            loadPrevAppointments() {
                let date;

                if (this.date) {
                    date = window.moment(this.date);
                } else {
                    date = window.moment().startOf('day');
                }

                date.subtract(5, 'day');

                // Because we hide Sundays, we need to subtract 1 more day if Sunday has been
                // hidden in the current view.
                if (DateTime.includesSunday(date)) {
                    date.subtract(1, 'day');
                }

                const now = window.moment().startOf('day');

                if (date.isBefore(now)) {
                    date = now;
                }

                this.dispatch('appointments.reload', { date });
            },

            loadNextAppointments() {
                let date, maxDate = window.moment().startOf('day').add(process.env.VUE_APP_MAX_DAYS, 'day');

                if (this.date) {
                    date = window.moment(this.date).startOf('day');
                } else {
                    date = window.moment().startOf('day');
                }

                // Because we hide Sundays, we need to add 1 more day if Sunday has been
                // hidden in the current view.
                if (DateTime.includesSunday(date)) {
                    date.add(6, 'day');
                } else {
                    date.add(5, 'day');
                }

                if (date.isAfter(maxDate)) {
                    date = maxDate;
                }

                this.dispatch('appointments.reload', { date });
            },
        },
    }
</script>
